/**
 * AgentPanel — Composant réutilisable pour interagir avec un agent IA.
 *
 * Props :
 *   - agent (string) : id de l'agent, doit exister dans config/agents.js
 *                      (depot, dal, suivi, admin_ventes, ...)
 *   - context (object, optional) : pré-contexte injecté dans le body POST
 *                      (ex: { reference: "TEF-DOS-2026-00207" })
 *   - onComplete (callback) : appelé avec la réponse finale { verdict, analysis, ... }
 *   - mode ("embedded" | "standalone") : embedded = pas de card wrapper, standalone = avec
 *   - tenantId (string, optional) : défaut "tef"
 *
 * Fonctionnalités :
 *   - Header : icône agent, nom, badge statut (Inactif / En cours / Terminé)
 *   - Conversation : messages user + agent avec bouton copier
 *   - Tool calls : affichage en direct pendant l'exécution
 *   - Saisie : textarea + envoyer (Ctrl+Enter)
 *   - Verdict : badge coloré + analyse + iterations + durée
 *   - NO localStorage / sessionStorage (state en mémoire)
 *
 * Règles respectées :
 *   - useCallback AVANT useEffect (TDZ)
 *   - config depuis config/agents.js (no-hardcode)
 *   - testids : agent-panel-{id}, agent-panel-send, agent-panel-verdict, ...
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { Send, Copy, Check, Loader2, Wrench, Clock, Repeat, Bot } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { getAgentConfig } from "../../config/agents";
import AgentMarkdown from "./AgentMarkdown";

const API = process.env.REACT_APP_BACKEND_URL || "";

function StatusBadge({ status, palette }) {
  const config = {
    idle: { label: "Inactif", className: "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] border-[var(--oh-border)]" },
    running: { label: "En cours…", className: "bg-[var(--oh-info-bg)] text-[var(--oh-info-text)] border-[var(--oh-info-border)]" },
    done: { label: "Terminé", className: palette.badge },
    error: { label: "Erreur", className: "bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] border-[var(--oh-error-border)]" },
  }[status] || { label: status, className: "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)]" };
  return (
    <Badge className={`${config.className} text-xs border`} data-testid="agent-panel-status">
      {status === "running" && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
      {config.label}
    </Badge>
  );
}

function Message({ role, content, onCopy }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`group max-w-[85%] rounded-lg px-3 py-2 text-sm break-words ${
          isUser
            ? "bg-[var(--oh-accent-bg)] text-[var(--oh-text)] border border-[var(--oh-accent)] whitespace-pre-wrap"
            : "bg-[var(--oh-bg-elevated)] text-[var(--oh-text)] border border-[var(--oh-border)]"
        }`}
      >
        {isUser ? <div>{content}</div> : <AgentMarkdown density="compact">{content}</AgentMarkdown>}
        {!isUser && content && (
          <button
            onClick={() => onCopy(content)}
            className="mt-1 text-xs text-[var(--oh-text-secondary)] hover:text-[var(--oh-text)] opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1"
            data-testid="agent-message-copy"
          >
            <Copy className="w-3 h-3" /> Copier
          </button>
        )}
      </div>
    </div>
  );
}

function ToolCallItem({ call, palette }) {
  return (
    <div className={`flex items-start gap-2 text-xs py-1.5 px-2 rounded ${palette.softBg} border ${palette.softBorder}`}>
      <Wrench className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${palette.accent}`} />
      <div className="min-w-0 flex-1">
        <span className="font-mono font-medium">{call.name || call.tool || "tool"}</span>
        {call.status === "running" && <span className="ml-1 text-[var(--oh-info-text)]">…</span>}
        {call.status === "done" && <Check className="inline w-3 h-3 ml-1 text-[var(--oh-success-text)]" />}
        {call.input && (
          <span className="text-[var(--oh-text-secondary)] ml-2">
            {typeof call.input === "string" ? call.input : JSON.stringify(call.input)}
          </span>
        )}
      </div>
    </div>
  );
}

function VerdictBanner({ verdict, verdictsConfig, iterations, durationMs, analysis }) {
  const vConfig = verdictsConfig[verdict] || { label: verdict, color: "bg-[var(--oh-bg-elevated)] text-[var(--oh-text-secondary)] border-[var(--oh-border)]" };
  return (
    <div className="mt-3 rounded-md border border-[var(--oh-border)] bg-[var(--oh-bg-surface)] p-3" data-testid="agent-panel-verdict">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className={`${vConfig.color} text-xs font-medium border`}>
          {vConfig.label}
        </Badge>
        {Number.isFinite(iterations) && (
          <span className="text-xs text-[var(--oh-text-secondary)] inline-flex items-center gap-1">
            <Repeat className="w-3 h-3" /> {iterations} iter
          </span>
        )}
        {Number.isFinite(durationMs) && (
          <span className="text-xs text-[var(--oh-text-secondary)] inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {(durationMs / 1000).toFixed(1)}s
          </span>
        )}
      </div>
      {analysis && (
        <div className="mt-2 text-[var(--oh-text-secondary)]">
          <AgentMarkdown density="compact">{analysis}</AgentMarkdown>
        </div>
      )}
    </div>
  );
}

export default function AgentPanel({
  agent,
  context = {},
  onComplete,
  mode = "standalone",
  tenantId = "tef",
}) {
  const config = getAgentConfig(agent);
  const Icon = config.icon || Bot;

  const [status, setStatus] = useState("idle"); // idle | running | done | error
  const [messages, setMessages] = useState([]);
  const [toolCalls, setToolCalls] = useState([]);
  const [input, setInput] = useState("");
  const [response, setResponse] = useState(null);
  const scrollRef = useRef(null);

  // Auto-scroll à chaque update (useCallback AVANT useEffect)
  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, toolCalls, scrollToBottom]);

  const handleCopy = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié");
    } catch {
      toast.error("Impossible de copier");
    }
  }, []);

  const buildHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return {
      "Content-Type": "application/json",
      "X-Tenant-ID": tenantId,
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [tenantId]);

  const runAgent = useCallback(async (userText) => {
    if (status === "running") return;
    const prompt = (userText || input || config.defaultPrompt || "").trim();
    if (!prompt && !context.reference) {
      toast.error("Saisis une instruction pour l'agent.");
      return;
    }

    setStatus("running");
    setToolCalls([]);
    setResponse(null);
    setMessages(prev => [...prev, { role: "user", content: prompt || config.defaultPrompt }]);

    try {
      const body = {
        reference: context.reference,
        message: prompt || config.defaultPrompt,
        ...context,
      };
      const res = await fetch(`${API}${config.endpoints.run}`, {
        method: "POST",
        headers: buildHeaders(),
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${errText || ""}`.trim());
      }
      const data = await res.json();

      // Afficher les tool_calls qui se sont exécutés
      if (Array.isArray(data.tool_calls_log)) {
        setToolCalls(data.tool_calls_log.map(t => ({
          name: t.name || t.tool || "tool",
          input: t.input || t.arguments || null,
          status: "done",
        })));
      }

      setMessages(prev => [...prev, { role: "agent", content: data.analysis || "(pas d'analyse)" }]);
      setResponse({
        verdict: data.verdict,
        analysis: data.analysis,
        iterations: data.iterations,
        duration_ms: data.duration_ms,
        session_id: data.session_id,
      });
      setStatus("done");
      setInput("");
      onComplete?.(data);
    } catch (err) {
      console.error(`[AgentPanel/${agent}] run error:`, err);
      setMessages(prev => [...prev, { role: "agent", content: `⚠ Erreur : ${err.message || err}` }]);
      setStatus("error");
      toast.error(`Agent ${config.label} en erreur`);
    }
  }, [status, input, context, config, buildHeaders, onComplete, agent]);

  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      runAgent();
    }
  }, [runAgent]);

  const wrapper = mode === "embedded"
    ? "flex flex-col h-full"
    : `flex flex-col h-full rounded-lg border ${config.palette.softBorder} ${config.palette.softBg} p-3`;

  return (
    <div className={wrapper} data-testid={`agent-panel-${agent}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 pb-2 border-b border-[var(--oh-border)]">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${config.palette.softBg} border ${config.palette.softBorder}`}>
            <Icon className={`w-4 h-4 ${config.palette.accent}`} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[var(--oh-text)] truncate">{config.label}</p>
            {context.reference && (
              <p className="text-xs font-mono text-[var(--oh-text-secondary)]">{context.reference}</p>
            )}
          </div>
        </div>
        <StatusBadge status={status} palette={config.palette} />
      </div>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto py-3 space-y-2 min-h-[200px]"
        data-testid="agent-panel-messages"
      >
        {messages.length === 0 && status === "idle" && (
          <div className="text-center text-xs text-[var(--oh-text-muted)] py-6">
            {config.description || "Envoie une instruction ou clique sur Lancer."}
          </div>
        )}
        {messages.map((m, i) => (
          <Message key={i} role={m.role} content={m.content} onCopy={handleCopy} />
        ))}
        {toolCalls.length > 0 && (
          <div className="space-y-1 mt-2" data-testid="agent-panel-toolcalls">
            {toolCalls.map((c, i) => <ToolCallItem key={i} call={c} palette={config.palette} />)}
          </div>
        )}
        {response && (
          <VerdictBanner
            verdict={response.verdict}
            verdictsConfig={config.verdicts}
            iterations={response.iterations}
            durationMs={response.duration_ms}
            analysis={response.analysis}
          />
        )}
      </div>

      {/* Input */}
      <div className="pt-2 border-t border-[var(--oh-border)]">
        <div className="flex items-end gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={config.defaultPrompt || "Instruction pour l'agent…"}
            rows={2}
            className="flex-1 resize-none text-sm bg-[var(--oh-bg-surface)] border-[var(--oh-border)]"
            data-testid="agent-panel-input"
          />
          <Button
            onClick={() => runAgent()}
            disabled={status === "running"}
            className={config.palette.button}
            data-testid="agent-panel-send"
          >
            {status === "running"
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[10px] text-[var(--oh-text-muted)] mt-1">Ctrl+Enter pour envoyer</p>
      </div>
    </div>
  );
}
